// @core/stores/project/ProjectSprintStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { projectApi } from '@core/api';
import type {
  AddItemToSprintRequest,
  BurndownResponse,
  CloneSprintRequest,
  CompleteSprintRequest,
  CreateSprintRequest,
  ItemResponse,
  Sprint,
  SprintResponse,
  StartSprintRequest,
  UpdateSprintRequest,
} from '@core/types/project';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class ProjectSprintStore {
  // List state
  items: Sprint[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Sprint | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Sprint>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Sprint | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Sprint | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Add item to sprint
   */
  async AddItemToSprint(project_id: string, sprint_id: string, data: AddItemToSprintRequest): Promise<ItemResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await projectApi.AddItemToSprint(project_id, sprint_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Clone sprint
   */
  async CloneSprint(project_id: string, sprint_id: string, data: CloneSprintRequest): Promise<SprintResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await projectApi.CloneSprint(project_id, sprint_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Complete sprint
   */
  async CompleteSprint(project_id: string, sprint_id: string, data: CompleteSprintRequest): Promise<SprintResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await projectApi.CompleteSprint(project_id, sprint_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Create sprint
   */
  async CreateSprint(project_id: string, data: CreateSprintRequest): Promise<Sprint> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await projectApi.CreateSprint(project_id, data);
      const item = response.sprint;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Delete sprint
   */
  async DeleteSprint(project_id: string, sprint_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await projectApi.DeleteSprint(project_id, sprint_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== sprint_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === sprint_id) {
          this.currentItem = null;
        }

        this.removeFromCache(sprint_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Get sprint
   */
  async GetSprint(project_id: string, sprint_id: string): Promise<Sprint> {
    // Check cache first
    const cached = this.cache.get(sprint_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await projectApi.GetSprint(project_id, sprint_id);
      const item = response.sprint;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * Get sprint burndown
   */
  async GetSprintBurndown(project_id: string, sprint_id: string): Promise<BurndownResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await projectApi.GetSprintBurndown(project_id, sprint_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * List sprints
   */
  async ListSprints(project_id: string, params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await projectApi.ListSprints(project_id, params);

      runInAction(() => {
        this.items = response.sprints?.sprints ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Remove item from sprint
   */
  async RemoveItemFromSprint(project_id: string, sprint_id: string, item_id: string): Promise<ItemResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await projectApi.RemoveItemFromSprint(project_id, sprint_id, item_id);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Start sprint
   */
  async StartSprint(project_id: string, sprint_id: string, data: StartSprintRequest): Promise<SprintResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await projectApi.StartSprint(project_id, sprint_id, data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update sprint
   */
  async UpdateSprint(project_id: string, sprint_id: string, data: UpdateSprintRequest): Promise<Sprint> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await projectApi.UpdateSprint(project_id, sprint_id, data);
      const item = response.sprint;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }
}

export const projectSprintStore = new ProjectSprintStore();
